import '@arco-design/web-react/dist/css/index.less'
import '@/style/globals.css'
import useI18n from '@/hooks/useI18n'
import { ComponentProps, Fragment, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ConfigProvider as ArcoProvider } from '@arco-design/web-react'
import useGetCsrfToken from '@/hooks/useGetCsrfToken'
import { StoreState, wrapper } from '@/store'
import { actions as ssrActions } from '@/store/ssr'
import { GlobalContext } from '@/context'
import userInfoCache from '@/utils/userInfoCache'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { GOOGLE_CLIENT_ID, SECONDARY_CURRENCY_COOKIE } from '@/utils/constants'
import Head from 'next/head'
import type { AppProps } from 'next/app'
import App from 'next/app'
import { useRouter } from 'next/router'
import Script from '@/components/Script'
import { getCookies } from 'cookies-next'
import generateCookies from '@/utils/generateCookies'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { CURRENCY_COOKIE, LANG_COOKIE, REGION_COOKIE, IS_FIRST_VISIT_COOKIE } from '@/utils/constants'
import { isMobile, isSSR } from '@/utils/is'
import Exception from '@/pages/exception'
import MobileException from '@/mobile-pages/exception'
import '@/style/global.less'
import '@/hooks/useNProgress/index.less'
import GuideToPC from '@/mobile-pages/guide-to-pc'
import { setCookieWithMaxAge } from '@/utils/cookie'
import { SEOConfig } from '@/components/SEOHeader'
import useLoading from '@/hooks/useLoading'
import GlobalLoading from '@/components/GlobalLoading'
import { RenderCanonical } from '@/utils/seo'
import useIntercom from '@/hooks/useIntercom'
import useInviteCode from '@/hooks/useInviteCode'
import useBdVid from '@/hooks/useBdVid'
import dynamic from 'next/dynamic'
import ErrorBoundary from '@/components/ErrorBoundary'
import useRouteBack from '@/hooks/useRouteBack'
import { Region } from '@/utils/region'
import NextScript from 'next/script'
import { SecondaryCurrency } from '@/utils/currency'

const MobileLayout = dynamic(() => import('@/mobile-components/MobileLayout'))
const PcLayout = dynamic(() => import('@/components/LayoutNew'))

dayjs.extend(relativeTime)

export type PageProps<P = unknown> = { isMobile?: boolean; ua?: string } & P

const CustomApp = (props: AppProps<{ errorCode?: ComponentProps<typeof Exception>['code']; message?: string }>) => {
  const { Component, pageProps } = props
  const {
    lang,
    currency,
    isMobile,
    showMemberModal: showMemberModalFromSSR,
    memberModalClosable: memberModalClosableFromSSR
  } = useSelector((state: StoreState) => state.ssr)

  const i18n = useI18n()
  dayjs.locale(i18n.dayjs)

  const router = useRouter()
  const [loading] = useLoading()
  // 获取token
  useGetCsrfToken()
  useInviteCode()
  useBdVid()

  const { isIntercomLoad } = useIntercom({ lang })

  useEffect(() => {
    // 2022.4.2版本起，前端不再缓存 userinfo，故将以前存在 localstorage 中的 userinfo cache 清楚
    if (userInfoCache.getCache()) {
      userInfoCache.resetCache()
    }

    // 清理过期的 localStorage 项，避免配额超限
    if (!isSSR) {
      try {
        const now = new Date().getTime()
        const keysToRemove: string[] = []

        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (!key) continue

          try {
            const itemStr = localStorage.getItem(key)
            if (!itemStr) continue

            const item = JSON.parse(itemStr)
            // 检查是否有 expiry 字段，且已过期
            if (item.expiry && now > item.expiry) {
              keysToRemove.push(key)
            }
          } catch (e) {
            // 忽略解析错误的项
            continue
          }
        }

        // 删除过期项
        keysToRemove.forEach((key) => {
          try {
            localStorage.removeItem(key)
          } catch (e) {
            // 静默失败
          }
        })

        if (keysToRemove.length > 0) {
          console.log(`App initialization: cleaned up ${keysToRemove.length} expired localStorage items`)
        }
      } catch (e) {
        // 静默失败，不影响应用启动
      }
    }
  }, [])

  const contextValue = {
    lang,
    currency,
    showMemberModalFromSSR,
    memberModalClosableFromSSR,
    isIntercomLoad
  }

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])
  // 监听回退事件
  useRouteBack()

  // TODO: 后端接口还未上线
  // useNotify()

  const renderHead = () => {
    return (
      <Head>
        <meta charSet="UTF-8" />
        <link rel="shortcut icon" type="image/x-icon" href="https://static.echotik.live/assets/icon.ico" />
        {RenderCanonical()}
        <meta httpEquiv="X-UA-Compatible" content="ie=edge" />
        <title>{SEOConfig.title}</title>
        <meta name="keywords" content={SEOConfig.keywords} />
        <meta name="description" content={SEOConfig.description} />
        {isMobile ? (
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no"
          />
        ) : (
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        )}
      </Head>
    )
  }

  const renderException = () => {
    return isMobile ? (
      <MobileException code={pageProps.errorCode!} message={pageProps?.message} />
    ) : (
      <Exception code={pageProps.errorCode!} message={pageProps?.message} />
    )
  }

  const renderPage = () => {
    const isAdaptMobile = [
      '/login',
      '/login/wx',
      '/register',
      '/onboarding',
      '/board',
      '/influencers/',
      '/products/',
      '/live/',
      '/videos/',
      '/shops/',
      '/influencers/leaderboard/',
      '/products/leaderboard/',
      '/search',
      '/user',
      '/pricing',
      '/index/product',
      'index/influencer',
      '/[influencers_category]/page',
      '/[products_category]/page',
      '/sitemap/',
      '/tool/ai',
      '/tools',
      '/tool/calculator'
    ].some((path) => router.pathname.startsWith(path))

    const hasLayout = ![
      '/login',
      '/login/wx',
      '/register',
      '/forgot-password',
      '/modify-password',
      '/onboarding',
      '/search'
    ].includes(router.pathname)

    const Layout = isMobile && isAdaptMobile ? MobileLayout : PcLayout
    const Wrapper = hasLayout ? Layout : Fragment
    const children = pageProps.errorCode ? renderException() : <Component {...pageProps} />

    return (
      <GlobalLoading loading={loading}>
        <Wrapper>{isMobile && !isAdaptMobile ? <GuideToPC>{children}</GuideToPC> : children}</Wrapper>
      </GlobalLoading>
    )
  }

  return (
    <ErrorBoundary>
      {renderHead()}
      {process.env.NODE_ENV === 'production' && process.env.NEXT_PUBLIC_BUILD_ENV === 'production' ? (
        <Script lang={lang} />
      ) : null}
      <NextScript strategy="lazyOnload" src="https://turing.captcha.qcloud.com/TJCaptcha.js" />
      <ArcoProvider
        locale={i18n.arco}
        componentConfig={{
          Card: {
            bordered: false
          },
          List: {
            bordered: false
          },
          Table: {
            border: false
          }
        }}
      >
        <GlobalContext.Provider value={contextValue}>
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{renderPage()}</GoogleOAuthProvider>
        </GlobalContext.Provider>
      </ArcoProvider>
    </ErrorBoundary>
  )
}

CustomApp.getInitialProps = wrapper.getInitialAppProps((store) => async (context) => {
  const cookies = getCookies(context.ctx)
  const token = cookies.token ? decodeURIComponent(cookies.token) : ''
  const isFirstVisit = cookies.is_first_visit

  const { language, currency, region, secondaryCurrency } = await generateCookies({
    acceptLanguage: (context.ctx.req?.headers['x-accept-language'] || context.ctx.req?.headers['accept-language']) as
      | string
      | undefined,
    languageFromCookie: cookies.lang,
    currencyFromCookie: cookies.currency,
    secondaryCurrencyFromCookie: cookies.secondary_currency,
    regionFromCookie: cookies.region,
    token
  })
  let currentRegion = region
  // 因为 AWS 服务器，复写了原始的 ua 头
  // 所以新增 x-user-agent，是由 nginx 层，复制出来 ua 头
  const ua = (context.ctx.req?.headers['x-user-agent'] || context.ctx.req?.headers['user-agent']) as string | undefined
  const { res, req } = context.ctx
  if (isFirstVisit !== 'false') {
    // 说明是第一次访问, region强制设置成US
    currentRegion = Region.US
    setCookieWithMaxAge(IS_FIRST_VISIT_COOKIE, 'false', { req, res })
    setCookieWithMaxAge(REGION_COOKIE, Region.US, { req, res })
  } else {
    setCookieWithMaxAge(REGION_COOKIE, currentRegion, { req, res })
  }

  store.dispatch(
    ssrActions.updateSsrStoreAction({
      token,
      lang: language,
      currency,
      region: currentRegion,
      secondaryCurrency: secondaryCurrency as SecondaryCurrency,
      isMobile: isMobile(ua),
      host: req?.headers.host,
      ua
    })
  )

  setCookieWithMaxAge(CURRENCY_COOKIE, currency, { req, res })
  setCookieWithMaxAge(SECONDARY_CURRENCY_COOKIE, secondaryCurrency, { req, res })
  setCookieWithMaxAge(LANG_COOKIE, language, { req, res })

  return await App.getInitialProps(context)
})

export default wrapper.withRedux(CustomApp)
